import React, {
  useMemo,
} from 'react';
import pick from 'lodash/pick';
import {
  billingEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  ChargesRowData,
} from 'pages/Dashboard/pages/Billing/types';
import {
  compactPatient,
} from 'pages/Dashboard/utils/helper';
import CollectIcon from 'core/icons/collectIcon';
import WriteOffIcon from 'core/icons/writeoffIcon';
import PaymentSuccessIcon from 'core/icons/paymentSuccess';
import WithdrawIcon from 'core/icons/withdraw';
import ActionMenu, {
  DropdownMenuItem,
} from 'core/components/DropdownMenu';
import noop from 'lodash/noop';
import {
  isNilOrZero,
} from '@populateui/utils';
import {
  ClaimSummaryDTO,
} from 'dtos';
import compact from 'lodash/compact';

type RowData = Partial<ChargesRowData>;

const iconClassName = '!w-4 !h-4 !text-blue-500';

const options: DropdownMenuItem<RowData>[] = [
  {
    id: 'collect-payment',
    label: 'Collect Payment',
    icon: CollectIcon,
    iconClassName,
    onSelect: (data) => {
      paymentsEventChannel.emit('onPaymentDialogOpen', {
        encounterId: data?.encounterId!,
        patient: data?.patient!,
      });
    },
  },
  {
    id: 'allocate-payments',
    label: 'Allocate Payments',
    icon: PaymentSuccessIcon,
    iconClassName,
    onSelect: (data) => {
      billingEventChannel.emit('onEncountersAllocation', { patient: data?.patient! });
    },
  },
  {
    id: 'add-write-off',
    label: 'Add Patient Write Off',
    icon: WriteOffIcon,
    iconClassName,
    onSelect: (data) => {
      paymentsEventChannel.emit('onAddWriteoff', {
        ...pick(data, ['encounterId', 'visitDateTime']),
        patient: compactPatient(data?.patient!),
      });
    },
  },
  {
    id: 'close-insurance-workflow',
    label: 'Resolve Insurance Balance',
    icon: WriteOffIcon,
    iconClassName,
    onSelect: (data) => {
      const claims = compact(
        (data?.claimCharges ?? [])
          .filter((claim) => !isNilOrZero(claim.claimId))
          .flatMap((claim) => [
            claim,
            claim.secondaryClaim,
          ]),
      );
      billingEventChannel.emit('onInsuranceResolve', {
        claims: claims as Partial<ClaimSummaryDTO>[],
        insuranceBalance: data?.insuranceBalance ?? 0,
        encounterId: data?.encounterId!,
        visitDateTime: data?.visitDateTime!,
        patient: data?.patient!,
      });
    },
  },
  {
    id: 'adjust-payment',
    label: 'Adjust payment',
    icon: WithdrawIcon,
    iconClassName,
    onSelect: (data) => {
      paymentsEventChannel.emit('onAdjustPayment', { patient: data?.patient! });
    },
  },
];

export default function RowActions({ isChild, ...data }: RowData) {
  const menuOptions = useMemo(() => options.map(
    (option) => ({
      ...option,
      disabled: option.id === 'close-insurance-workflow' && (data?.insuranceBalance ?? 0) === 0,
    }),
  ), [data]);

  return !isChild ? (
    <ActionMenu
      trigger={(
        <div
          role="button"
          tabIndex={-1}
          onKeyDown={noop}
          onClick={(e) => { e.stopPropagation(); }}
          className="cursor-pointer p-2"
        >
          <MoreVertIcon className="!w-5 !h-5 !text-gray-500" />
        </div>
      )}
      items={menuOptions}
      data={data}
      openOnHover
      offset={[0, 6]}
      paperClassName="!min-w-[11rem] nw-create-options"
      placement="top"
    />
  ) : <div />;
}
